"use client";

import { useRef, useState, useEffect } from "react";
import { Command } from "lucide-react";
import { AuroraBackground } from "./aurora-background";
import { FocusedAuroraBackground } from "./focused-aurora-background";
import { GlowingEffect } from "../global-ui/glowing-effect";
import GiveawayPreviewDummy from "./bento-grid-sections/giveaway-preview-dummy";
import PersonaGeneratorPreview from "./bento-grid-sections/persona-generator-preview";
import ContentGenerationPreview from "./bento-grid-sections/content-generation-preview";
import DummyPromoVideo from "./bento-grid-sections/dummy-promo-video";
import DottedBackground from "../global-ui/dotted-background";
import { useMediaQuery } from "usehooks-ts";
import { useInView } from "framer-motion";
export default function AuroraBentoGrid() {
  const containerRef = useRef<HTMLDivElement>(null);
  const mainContainerRef = useRef<HTMLDivElement>(null);
  const [mounted, setMounted] = useState(false);
  const isMobileQuery = useMediaQuery("(max-width: 768px)");
  const isInView = useInView(mainContainerRef, {
    margin: "0px 100px -50px 0px"
  });

  // Only use the media query result after mounting to prevent hydration issues
  const isMobile = mounted ? isMobileQuery : false;
  useEffect(() => {
    setMounted(true);
  }, []);
  const [variant, setVariant] = useState<string>("default");
  const [animationSpeed, setAnimationSpeed] = useState<string>("normal");
  const [intensity, setIntensity] = useState<string>("high");
  const [rotation, setRotation] = useState<number>(315);
  const [backgroundColor, setBackgroundColor] = useState("#000");
  const [primaryColor, setPrimaryColor] = useState("");
  const [secondaryColor, setSecondaryColor] = useState("");
  const [disableHueRotation, setDisableHueRotation] = useState(false);
  const [disableGradientMovement, setDisableGradientMovement] = useState(false);
  return <div ref={mainContainerRef} className="relative bg-transparent" data-sentry-component="AuroraBentoGrid" data-sentry-source-file="aurora-bento-grid.tsx">
      {/* Top Fade Overlay */}
      {/* <div className="pointer-events-none absolute inset-x-0 left-0 top-0 z-0 w-full bg-gradient-to-b from-[#09090B] via-[#09090B] to-black pb-10 dark:h-44" /> */}

      {/* Bottom Fade Overlay */}
      {/* <div className="pointer-events-none absolute inset-x-0 bottom-0 left-0 z-0 w-full bg-gradient-to-t from-[#09090B] via-[#09090B] to-black dark:h-44" /> */}

      <div className="flex w-screen flex-col items-center justify-center bg-[#09090B] px-10 pb-32 pt-20 text-white md:px-0">
        <div className="z-[5] mx-auto mt-10 max-w-6xl text-white ">
          <div className="mb-16 text-center">
            <h1 className="mb-6 text-4xl font-bold text-white md:text-5xl">
              Your go-to-market team in a box
            </h1>
            <p className="mx-auto max-w-2xl text-lg text-gray-400 md:text-xl">
              Everything you need to get your first 100 customers fast.
            </p>
          </div>

          <div ref={containerRef} className="relative grid w-full grid-cols-1 gap-4 md:grid-cols-3 md:gap-6">
            {/* Persona Generator Card */}
            <div className="bento-card relative h-[420px] overflow-hidden rounded-3xl">
              <div className="relative h-full rounded-3xl border border-amber-700/20 p-0">
                {isInView && <GlowingEffect spread={40} glow={false} disabled={isMobile} proximity={64} inactiveZone={0.01} theme="white" borderWidth={2} movementDuration={1} blur={1} className="" />}
                <div className="flex h-[418px] min-h-[418px] flex-col overflow-clip rounded-3xl">
                  {isInView ? <FocusedAuroraBackground beamIntensity="vlow">
                      <PersonaGeneratorPreview />
                    </FocusedAuroraBackground> : <PersonaGeneratorPreview />}
                </div>
              </div>
            </div>

            {/* Content Generation Card */}
            <div className="bento-card group relative h-[420px] overflow-hidden rounded-3xl md:col-span-2">
              <div className="relative h-full rounded-3xl border border-amber-700/20 p-0">
                {isInView && <GlowingEffect spread={40} glow={false} disabled={isMobile} proximity={64} inactiveZone={0.01} theme="white" borderWidth={2} movementDuration={1} blur={1} />}
                {isInView ? <AuroraBackground theme="sunset" direction="reversed" placement="top-left" auroraClassName="group-hover:opacity-100" className="bg-zinc-950" disableAnimations={isMobile}>
                    {/* <DottedBackground
                     dotColor="#f97316"
                     backgroundColor="transparent"
                     enableVignette={true}
                     vignetteColor="rgba(0,0,0,0.9)"
                     enableInnerGlow={true}
                     innerGlowColor="rgba(0,0,0,0.8)"
                     dotSize={1}
                     dotSpacing={4}
                     className="absolute inset-0 left-0 top-0 z-[10] opacity-0 transition-opacity duration-700 group-hover:opacity-30"
                     /> */}
                    <ContentGenerationPreview />
                  </AuroraBackground> : <ContentGenerationPreview />}
              </div>
            </div>

            {/* Giveaway Card */}
            <div className="bento-card group relative h-[420px] overflow-hidden rounded-3xl md:col-span-2">
              <div className="relative h-full rounded-3xl border border-amber-700/20 p-0">
                {isInView && <GlowingEffect spread={40} glow={false} disabled={isMobile} proximity={64} inactiveZone={0.01} theme="white" borderWidth={2} movementDuration={1} blur={1} />}
                {isInView ? <AuroraBackground theme="sunset" direction="default" auroraClassName="group-hover:opacity-100" className="bg-zinc-950" disableAnimations={isMobile}>
                    <GiveawayPreviewDummy />
                  </AuroraBackground> : <GiveawayPreviewDummy />}
              </div>
            </div>

            {/* Promo Videos Card */}
            <div className="bento-card group relative h-[420px] overflow-hidden rounded-3xl">
              <div className="relative h-full rounded-3xl border border-amber-700/20 p-0">
                {isInView && <GlowingEffect spread={40} glow={false} disabled={isMobile} proximity={64} inactiveZone={0.01} theme="white" borderWidth={2} movementDuration={1} blur={1} />}
                {isInView ? <FocusedAuroraBackground beamIntensity="medium">
                    {/* <DottedVignetteBackground
                     dotColor="#f97316"
                     backgroundColor="transparent"
                     enableVignette={true}
                     vignetteColor="rgba(0,0,0,0.9)"
                     enableInnerGlow={true}
                     innerGlowColor="rgba(0,0,0,0.8)"
                     enableRipple={false}
                     dotSize={1}
                     dotSpacing={4}
                     className="opacity-20 transition-opacity duration-700 group-hover:opacity-60"
                     /> */}

                    <DottedBackground variant={variant as any} animationSpeed={animationSpeed as any} intensity={"low"} rotation={rotation as any} backgroundColor={backgroundColor} primaryColor={primaryColor || undefined} secondaryColor={secondaryColor || undefined} disableHueRotation={isMobile} disableGradientMovement={isMobile} enableCircleOverlay={false} enableInnerGlow={true} innerGlowColor="#000000" dotZIndex={3} // Dots in middle
                innerGlowZIndex={8} // Inner glow below vignette
                innerGlowOpacity={1} innerGlowSize={40} enableVignette={false} vignetteColor="rgba(0,0,0,0.9)" vignetteOpacity={0.9} innerGlowCoreSize={25} // Solid white up to 25%
                dotSize={1.5} dotSpacing={6} className="z-0 opacity-0 transition-opacity duration-700 md:group-hover:opacity-30" />
                    <DummyPromoVideo />
                  </FocusedAuroraBackground> : <DummyPromoVideo />}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Gradient: black to transparent */}
      {/* <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-b from-black to-transparent" /> */}

      <style jsx global>{`
        .bento-card {
          isolation: isolate;
        }

        .bento-card .glow {
          z-index: 1;
        }

        .bento-card:hover .glow::after {
          opacity: 1 !important;
        }
      `}</style>
    </div>;
}